/**
 * The objection, staged: a misclick-prevention confirm, then Grounds, then
 * the vote — the client side of the redesign described in full in
 * `apps/server/src/director/objections.ts`'s own module docstring.
 *
 * Takes what it renders as props rather than reading the store, like every
 * other panel here (`DigestPanel.tsx`'s own comment explains why: zustand
 * serves the *initial* state to a server render, so a component that
 * subscribed could only ever be tested empty).
 */

import { useEffect, useRef, useState } from 'react';

/**
 * A live countdown against a server-pushed deadline.
 *
 * **A deliberate departure from this codebase's "no countdown against a
 * server deadline" stance** — the old appeal/proposal windows (5/10 minutes)
 * read fine as a static banner; at 5s/45s/60s, a silent deadline reads as
 * broken. The server stays sole authority for actually closing a stage —
 * this number is cosmetic, and a refusal from the server always wins over
 * whatever this shows.
 *
 * **Wall-clock, not a decrementing counter** — `deadlineTs - now`, re-read
 * every tick — so a throttled background tab still shows correct remaining
 * time on its next paint, the same shape `NewCampaignPage.tsx`'s own
 * premise-suggestion cooldown already uses.
 */
export function StageCountdown({ deadlineTs }: { readonly deadlineTs: number }) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (Date.now() >= deadlineTs) return;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [deadlineTs]);
  const secondsLeft = Math.max(0, Math.ceil((deadlineTs - now) / 1000));
  return <span>{secondsLeft}s</span>;
}

/** How long Stage 1's confirm stays open before it auto-cancels. */
const CONFIRM_MS = 5_000;

/**
 * Stage 1 — the misclick-prevention confirm.
 *
 * **Pure client-side UI with no server-tracked state of its own.** A
 * cancelled or timed-out confirm costs nothing and never reaches the
 * per-beat-ever objection slot — only "Yes, object" does, by calling
 * `onConfirm`. **Auto-cancels, never auto-confirms** — silence has to mean
 * "didn't happen", the same way an unclear `dangerPassed` directive
 * defaults to "no" elsewhere in this codebase.
 *
 * The claim (`onClaim`, fired once on open) is what makes the *next* few
 * seconds safe: it proves to the server, right now, that the click was
 * timely, so this dialog can take its own time without losing to the
 * Object link's 60-second window.
 */
export function ObjectionConfirmDialog({
  claim,
  onClaim,
  onConfirm,
  onCancel,
}: {
  readonly claim: { readonly ok: boolean; readonly reason?: string } | null;
  readonly onClaim: () => void;
  readonly onConfirm: () => void;
  readonly onCancel: () => void;
}) {
  const [expired, setExpired] = useState(false);

  // Fired once, the instant this dialog opens — mirroring the Object link's
  // own click, not deferred to any later moment.
  useEffect(() => {
    onClaim();
  }, []);

  useEffect(() => {
    if (expired || claim?.ok === false) return;
    const id = setTimeout(() => setExpired(true), CONFIRM_MS);
    return () => clearTimeout(id);
  }, [expired, claim?.ok]);

  const refused = claim?.ok === false;

  return (
    <div
      role="dialog"
      aria-label="Object to this beat?"
      aria-live="polite"
      className="rounded-md border border-sky-700/50 p-2 text-xs text-sky-900 dark:text-sky-200"
    >
      {refused ? (
        <>
          <p>Too late — {claim.reason}</p>
          <button
            type="button"
            onClick={onCancel}
            className="mt-1 rounded-md border border-neutral-400/60 px-2 py-0.5"
          >
            Close
          </button>
        </>
      ) : expired ? (
        <>
          <p>That expired. Object again if it still matters.</p>
          <button
            type="button"
            onClick={onCancel}
            className="mt-1 rounded-md border border-neutral-400/60 px-2 py-0.5"
          >
            Close
          </button>
        </>
      ) : (
        <>
          <p>Object to this?</p>
          <div className="mt-1 flex gap-2">
            <button
              type="button"
              onClick={onConfirm}
              className="rounded-md border border-sky-700/60 px-2 py-0.5"
            >
              Yes, object
            </button>
            <button
              type="button"
              onClick={onCancel}
              className="rounded-md border border-neutral-400/60 px-2 py-0.5"
            >
              Cancel
            </button>
          </div>
        </>
      )}
    </div>
  );
}

export interface OpenObjectionView {
  readonly objectionId: string;
  readonly messageId: string;
  readonly stage: 'grounds' | 'voting';
  readonly appellant: string;
  readonly beat?: string;
  readonly reason?: string;
  readonly stageDeadlineTs: number;
  readonly voted?: boolean;
}

/** How much of a Grounds submission the server actually keeps. */
const GROUNDS_MAX = 300;

/**
 * Stage 2, for the appellant: a bounded free-text entry.
 *
 * **The deadline drives an actual auto-submit, not just a display.** A
 * `setTimeout` keyed to the server's own `deadlineTs` fires `onSubmit` with
 * whatever is currently typed — read through a ref, not the effect's own
 * closed-over `text`, or the timer would submit whatever was typed at the
 * moment the field opened rather than the moment it fires. Manual "Submit"
 * and the timeout are the same call, just triggered differently, matching
 * `submitObjectionGrounds`'s own docstring on the server.
 *
 * `submitted` is local-only, to stop a double-submit attempt in the brief
 * window between clicking Submit (or the timer firing) and `stage` actually
 * flipping to `'voting'` server-side — the server's own atomic `settling`
 * claim already makes a second submit harmless, this is just about not
 * inviting one.
 */
function GroundsEntry({
  deadlineTs,
  onSubmit,
}: {
  readonly deadlineTs: number;
  readonly onSubmit: (grounds: string) => void;
}) {
  const [text, setText] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const latest = useRef('');
  latest.current = text;

  useEffect(() => {
    const msLeft = deadlineTs - Date.now();
    if (msLeft <= 0) return;
    const id = setTimeout(() => {
      setSubmitted(true);
      onSubmit(latest.current);
    }, msLeft);
    return () => clearTimeout(id);
  }, [deadlineTs]);

  if (submitted) {
    return <p className="mt-1">Grounds submitted. Waiting for the vote to open…</p>;
  }

  return (
    <div className="mt-1">
      <textarea
        value={text}
        onChange={(event) => setText(event.target.value.slice(0, GROUNDS_MAX))}
        maxLength={GROUNDS_MAX}
        rows={2}
        placeholder="Grounds (optional)"
        aria-label="Grounds for this objection"
        className="w-full rounded-md border border-sky-700/60 bg-transparent p-1 text-xs"
      />
      <div className="mt-1 flex items-center justify-between">
        <span className="text-neutral-500 dark:text-neutral-400">
          Auto-submits in <StageCountdown deadlineTs={deadlineTs} />
        </span>
        <button
          type="button"
          onClick={() => {
            setSubmitted(true);
            onSubmit(text);
          }}
          className="rounded-md border border-sky-700/60 px-2 py-0.5"
        >
          Submit
        </button>
      </div>
    </div>
  );
}

/**
 * The disclaimer, verbatim, wherever a decision is being made about an
 * objection. Fixed text — the effect is the same for every objection, so
 * this never needs to vary per beat.
 */
function BoundedEffectNotice() {
  return (
    <p className="mt-1 text-neutral-500 dark:text-neutral-400">
      What this decides: whether the DM keeps treating this line as true going forward. It does
      not undo anything already recorded — no items, damage, XP, or other results reverse, even
      if this passes.
    </p>
  );
}

/**
 * Stages 2 and 3 — Grounds, then the vote.
 *
 * **Sustain/Overrule only ever render once `stage === 'voting'`.** Voting
 * during `'grounds'` is refused server-side with "that vote has already
 * closed" — a confusing message for a vote that has not started — so the
 * client must not invite it.
 *
 * **The appellant, and only the appellant, sees the real Grounds field.**
 * Identified by comparing `myName` to `openObjection.appellant` — the only
 * identity the server hands this panel, since `objection:open`'s payload
 * carries a name, not a character id. Everyone else in the beat's audience
 * sees the paused "Grounds pending" holding line instead, with the same
 * countdown and disclaimer, so nobody mistakes someone else's typing window
 * for their own.
 */
export function ObjectionPanel({
  openObjection,
  objectionNotice,
  myName,
  onSubmitGrounds,
  onVote,
}: {
  readonly openObjection: OpenObjectionView | null;
  readonly objectionNotice: string | null;
  readonly myName: string | null;
  readonly onSubmitGrounds: (objectionId: string, grounds: string) => void;
  readonly onVote: (sustain: boolean) => void;
}) {
  const amAppellant = openObjection !== null && myName !== null && myName === openObjection.appellant;

  return (
    <>
      {openObjection && openObjection.stage === 'grounds' && (
        <div
          aria-live="polite"
          className="rounded-md border border-sky-700/50 p-2 text-xs text-sky-900 dark:text-sky-200"
        >
          {amAppellant ? (
            <>
              <p>You object to a beat. State your grounds, or let it auto-submit empty.</p>
              <GroundsEntry
                deadlineTs={openObjection.stageDeadlineTs}
                onSubmit={(grounds) => onSubmitGrounds(openObjection.objectionId, grounds)}
              />
            </>
          ) : (
            <p>
              <strong>{openObjection.appellant}</strong> objects to a beat. Grounds pending —{' '}
              <StageCountdown deadlineTs={openObjection.stageDeadlineTs} />
            </p>
          )}
          <BoundedEffectNotice />
        </div>
      )}
      {openObjection && openObjection.stage === 'voting' && (
        <div
          aria-live="polite"
          className="rounded-md border border-sky-700/50 p-2 text-xs text-sky-900 dark:text-sky-200"
        >
          <p>
            <strong>{openObjection.appellant}</strong> objects to a beat.
            {openObjection.reason ? ` Grounds given: ${openObjection.reason}` : ' No grounds given.'}
          </p>
          {openObjection.beat && (
            <p className="mt-1 italic text-neutral-600 dark:text-neutral-400">"{openObjection.beat}"</p>
          )}
          <BoundedEffectNotice />
          <p className="mt-1 text-neutral-500 dark:text-neutral-400">
            A majority of those present sustains it. A tie, or nobody voting, leaves it standing —{' '}
            <StageCountdown deadlineTs={openObjection.stageDeadlineTs} />
          </p>
          {openObjection.voted ? (
            <p className="mt-1">Your vote is in.</p>
          ) : (
            <div className="mt-1 flex gap-2">
              <button
                type="button"
                onClick={() => onVote(true)}
                className="rounded-md border border-sky-700/60 px-2 py-0.5"
              >
                Sustain
              </button>
              <button
                type="button"
                onClick={() => onVote(false)}
                className="rounded-md border border-neutral-400/60 px-2 py-0.5"
              >
                Overrule
              </button>
            </div>
          )}
        </div>
      )}
      {objectionNotice && <p className="text-xs text-sky-800 dark:text-sky-300">{objectionNotice}</p>}
    </>
  );
}
